/**
 * MediaUploadGrid Component
 * Grid-based media management with upload and URL input support.
 * Displays existing media items with remove buttons and an "add more" card.
 */

import React, { useState, useRef } from 'react';
import { X, Upload, Link, Loader2, Image, Plus, Pencil, Check, Play, Video } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = import.meta.env.VITE_API_URL || '';

interface MediaUploadGridProps {
  /** Array of media URLs */
  mediaUrls: string[];
  /** Callback when media is added (url is the displayable URL, path is the storage path) */
  onMediaAdd: (url: string, path?: string) => void;
  /** Callback when media is removed by index */
  onMediaRemove: (index: number) => void;
  /** Optional: replace media at index with a new URL (same slot, preserves order) */
  onMediaReplace?: (index: number, newUrl: string) => void;
  /** Optional: reorder media by moving item from one index to another */
  onMediaReorder?: (fromIndex: number, toIndex: number) => void;
  /** Maximum number of media items allowed */
  maxItems?: number;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Compact mode for smaller thumbnails (option-level) */
  compact?: boolean;
}

type AddMode = 'none' | 'url' | 'upload';

/** Exported for read-only previews (e.g. SurveyQuestionsPreview). */
export function getMediaTypeFromUrl(url: string): 'image' | 'video' | 'unknown' {
  if (!url) return 'unknown';
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be') || lowerUrl.includes('vimeo.com')) {
    return 'video';
  }
  if (/\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url)) {
    return 'video';
  }
  if (/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url)) {
    return 'image';
  }
  return 'image';
}

/** YouTube/Vimeo URLs are not playable with a file-style video element. */
export function isExternalStreamingVideoUrl(url: string): boolean {
  const lower = url.toLowerCase();
  return (
    lower.includes('youtube.com') ||
    lower.includes('youtu.be') ||
    lower.includes('vimeo.com')
  );
}

export function getYouTubeThumbnail(url: string): string | null {
  let videoId: string | null = null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      videoId = u.pathname.slice(1);
    } else if (u.pathname.includes('/embed/')) {
      videoId = u.pathname.split('/embed/')[1]?.split(/[?/]/)[0] || null;
    } else {
      videoId = u.searchParams.get('v');
    }
  } catch { /* ignore */ }
  return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
}

export function getEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (u.hostname.includes('youtube.com') && u.pathname.includes('/embed/')) {
      return url;
    }
    if (u.hostname.includes('youtube.com') && u.searchParams.get('v')) {
      return `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
    }
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch { /* ignore */ }
  return null;
}

function SortableMediaItem({ id, children }: { id: string; children: React.ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: 'transform 0ms',
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

const MediaUploadGrid: React.FC<MediaUploadGridProps> = ({
  mediaUrls,
  onMediaAdd,
  onMediaRemove,
  onMediaReplace,
  onMediaReorder,
  maxItems = 5,
  disabled = false,
  compact = false
}) => {
  // Auto-show upload UI when no media exists
  const [addMode, setAddMode] = useState<AddMode>(mediaUrls.length === 0 ? 'upload' : 'none');
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editUrlValue, setEditUrlValue] = useState('');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onMediaReorder) return;
    const fromIndex = mediaUrls.findIndex((_, i) => `media-${i}` === active.id);
    const toIndex = mediaUrls.findIndex((_, i) => `media-${i}` === over.id);
    if (fromIndex !== -1 && toIndex !== -1) {
      onMediaReorder(fromIndex, toIndex);
    }
  };

  const isValidEditedUrl = (s: string): boolean => {
    const t = s.trim();
    if (!t) return false;
    if (t.startsWith('/')) return true;
    try {
      new URL(t);
      return true;
    } catch {
      return false;
    }
  };

  const commitMediaUrlEdit = () => {
    if (editingIndex === null || !onMediaReplace) {
      setEditingIndex(null);
      return;
    }
    const idx = editingIndex;
    const trimmed = editUrlValue.trim();
    if (!trimmed) {
      setError('URL cannot be empty');
      return;
    }
    if (!isValidEditedUrl(trimmed)) {
      setError('Please enter a valid URL or path');
      return;
    }
    const current = mediaUrls[idx];
    if (trimmed !== current) {
      onMediaReplace(idx, trimmed);
    }
    setEditingIndex(null);
    setEditUrlValue('');
    setError(null);
  };

  const cancelMediaUrlEdit = () => {
    setEditingIndex(null);
    setEditUrlValue('');
  };

  const canAddMore = mediaUrls.length < maxItems && !disabled;

  // Build full URL for display
  const getFullUrl = (url: string): string => {
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${API_URL}${url}`;
    return url;
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const availableSlots = maxItems - mediaUrls.length;
    
    if (availableSlots <= 0) {
      setError(`Maximum of ${maxItems} media items reached`);
      return;
    }

    const filesToUpload = fileArray.slice(0, availableSlots);
    const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    const videoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    const validTypes = [...imageTypes, ...videoTypes];
    const maxImageSize = 15 * 1024 * 1024; // 15MB
    const maxVideoSize = 100 * 1024 * 1024; // 100MB

    for (const file of filesToUpload) {
      if (!validTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Please upload PNG, JPG, GIF, WEBP, MP4, WEBM, or MOV`);
        return;
      }
      const isVideo = videoTypes.includes(file.type);
      const maxSize = isVideo ? maxVideoSize : maxImageSize;
      if (file.size > maxSize) {
        setError(
          isVideo
            ? `File ${file.name} exceeds 100MB limit for video`
            : `File ${file.name} exceeds 15MB limit`
        );
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const totalSize = filesToUpload.reduce((s, f) => s + f.size, 0);
    const perFileLoaded = new Array(filesToUpload.length).fill(0);

    const uploadOne = (file: File, idx: number): Promise<any> =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/question-images/upload`);
        xhr.withCredentials = true;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            perFileLoaded[idx] = e.loaded;
            const total = perFileLoaded.reduce((a, b) => a + b, 0);
            setUploadProgress(Math.round((total / totalSize) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error('Invalid response')); }
          } else {
            try {
              const err = JSON.parse(xhr.responseText);
              reject(new Error(err.detail || err.error || 'Upload failed'));
            } catch { reject(new Error(`Upload failed (${xhr.status})`)); }
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        const formData = new FormData();
        formData.append('file', file);
        xhr.send(formData);
      });

    try {
      const results = await Promise.all(filesToUpload.map((f, i) => uploadOne(f, i)));

      results.forEach(data => {
        onMediaAdd(data.image_url, data.image_path);
      });

      setAddMode('none');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle URL submission
  const handleUrlSubmit = () => {
    const trimmedUrl = urlInput.trim();
    if (!trimmedUrl) {
      setError('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(trimmedUrl);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    onMediaAdd(trimmedUrl);
    setUrlInput('');
    setAddMode('none');
    setError(null);
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && canAddMore) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || !canAddMore) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };

  const thumbnailSize = compact ? 'w-20 h-20' : 'w-28 h-28';
  const gridCols = compact ? 'grid-cols-4' : 'grid-cols-3 md:grid-cols-4';

  return (
    <div className="space-y-2">
      {/* Media Grid */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={mediaUrls.map((_, i) => `media-${i}`)} strategy={rectSortingStrategy}>
      <div className={`grid ${gridCols} gap-2`}>
        {/* Existing Media Items */}
        {mediaUrls.map((url, index) => {
          const mediaType = getMediaTypeFromUrl(url);
          const fullUrl = getFullUrl(url);
          const isEditing = editingIndex === index;
          const canDrag = !disabled && !isEditing && !!onMediaReorder && mediaUrls.length > 1;

          const thumbnail = (
            <div
              className={`relative group ${thumbnailSize} rounded-lg border border-gray-200 overflow-hidden bg-gray-50 ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
            >
              {mediaType === 'image' ? (
                <img
                  src={fullUrl}
                  alt={`Media ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg></div>`;
                  }}
                />
              ) : mediaType === 'video' ? (
                isExternalStreamingVideoUrl(url) ? (
                  (() => {
                    const thumb = getYouTubeThumbnail(url);
                    return (
                      <button
                        type="button"
                        onClick={() => setVideoPreviewUrl(getEmbedUrl(url) || fullUrl)}
                        className="w-full h-full relative bg-gray-900 flex items-center justify-center"
                      >
                        {thumb ? (
                          <img src={thumb} alt="Video" className="w-full h-full object-cover opacity-80" />
                        ) : (
                          <div className="w-full h-full bg-purple-50" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="w-6 h-6 text-white drop-shadow-lg" fill="white" />
                        </div>
                      </button>
                    );
                  })()
                ) : (
                  <button
                    type="button"
                    onClick={() => setVideoPreviewUrl(fullUrl)}
                    className="w-full h-full relative bg-gradient-to-br from-gray-700 to-gray-900 flex flex-col items-center justify-center gap-1"
                  >
                    <Video className="w-5 h-5 text-gray-400" />
                    <span className="text-[9px] text-gray-400 truncate max-w-[90%] px-1">
                      {url.split('/').pop()?.split('?')[0] || 'video'}
                    </span>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Play className="w-6 h-6 text-white drop-shadow-lg" fill="white" />
                    </div>
                  </button>
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-6 h-6 text-gray-400" />
                </div>
              )}

              {/* Edit URL */}
              {!disabled && onMediaReplace && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingIndex(index);
                    setEditUrlValue(url);
                    setError(null);
                  }}
                  className="absolute top-1 left-1 w-5 h-5 bg-white/90 text-gray-700 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-white border border-gray-200"
                  title="Edit URL"
                >
                  <Pencil className="w-2.5 h-2.5" />
                </button>
              )}

              {/* Remove Button */}
              {!disabled && !isEditing && (
                <button
                  type="button"
                  onClick={() => onMediaRemove(index)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                  title="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
              )}

              {/* Index Badge */}
              {!isEditing && (
                <div className="absolute bottom-1 left-1 w-5 h-5 bg-black/60 text-white text-xs rounded flex items-center justify-center">
                  {index + 1}
                </div>
              )}
            </div>
          );

          return canDrag ? (
            <SortableMediaItem key={`media-${index}`} id={`media-${index}`}>
              {thumbnail}
            </SortableMediaItem>
          ) : (
            <React.Fragment key={`media-${index}`}>{thumbnail}</React.Fragment>
          );
        })}

        {/* Add More Card */}
        {canAddMore && addMode === 'none' && (
          <div
            onClick={() => setAddMode('upload')}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              ${thumbnailSize} rounded-lg border-2 border-dashed cursor-pointer
              flex flex-col items-center justify-center gap-1 transition-all
              ${isDragging 
                ? 'border-teal-500 bg-teal-50' 
                : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'
              }
              ${uploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-0.5">
                <Loader2 className="w-4 h-4 text-teal-500 animate-spin" />
                <span className="text-[10px] text-teal-600 font-medium">{uploadProgress}%</span>
              </div>
            ) : (
              <>
                <Plus className="w-5 h-5 text-gray-400" />
                <span className="text-xs text-gray-500">Add</span>
              </>
            )}
          </div>
        )}
      </div>
      </SortableContext>
      </DndContext>

      {/* Edit URL Bar - shown below grid when editing */}
      {editingIndex !== null && onMediaReplace && (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-2">
          <span className="text-xs text-gray-500 shrink-0">#{editingIndex + 1}</span>
          <input
            ref={editInputRef}
            type="text"
            value={editUrlValue}
            onChange={(e) => setEditUrlValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitMediaUrlEdit(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelMediaUrlEdit(); }
            }}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="https://..."
            autoFocus
          />
          <button
            type="button"
            onClick={commitMediaUrlEdit}
            className="px-3 py-1.5 bg-teal-500 text-white text-sm font-medium rounded-md hover:bg-teal-600 transition-colors flex items-center gap-1"
          >
            <Check className="w-3.5 h-3.5" />
            Save
          </button>
          <button
            type="button"
            onClick={cancelMediaUrlEdit}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Video Preview Modal */}
      {videoPreviewUrl && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setVideoPreviewUrl(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-8 pointer-events-none">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-2xl w-full pointer-events-auto">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-700">Video Preview</span>
                <button
                  type="button"
                  onClick={() => setVideoPreviewUrl(null)}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="aspect-video">
                {isExternalStreamingVideoUrl(videoPreviewUrl) ? (
                  <iframe
                    src={videoPreviewUrl}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={videoPreviewUrl}
                    controls
                    autoPlay
                    className="w-full h-full bg-black"
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Mode UI */}
      {addMode !== 'none' && canAddMore && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
          {/* Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => { setAddMode('upload'); setError(null); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                addMode === 'upload'
                  ? 'bg-teal-500 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            <button
              onClick={() => { setAddMode('url'); setError(null); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                addMode === 'url'
                  ? 'bg-teal-500 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Link className="w-4 h-4" />
              URL
            </button>
          </div>

          {/* Upload Mode */}
          {addMode === 'upload' && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                className="hidden"
                multiple
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                  h-20 border-2 border-dashed rounded-lg cursor-pointer
                  flex flex-col items-center justify-center gap-1 transition-all
                  ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400'}
                  ${uploading ? 'pointer-events-none' : ''}
                `}
              >
                {uploading ? (
                  <div className="w-full px-4 flex flex-col items-center gap-1.5">
                    <Loader2 className="w-5 h-5 text-teal-500 animate-spin" />
                    <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-teal-500 h-full rounded-full transition-all duration-200"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-teal-600 font-medium">{uploadProgress}%</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-gray-400" />
                    <p className="text-xs text-gray-500">Drop files or click to upload</p>
                    <p className="text-xs text-gray-400">Images max 15MB · Video MP4/WEBM/MOV max 100MB</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* URL Mode */}
          {addMode === 'url' && (
            <div className="flex gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                autoFocus
              />
              <button
                onClick={handleUrlSubmit}
                className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-md hover:bg-teal-600 transition-colors"
              >
                Add
              </button>
            </div>
          )}

          {/* Cancel Button */}
          <button
            onClick={() => { setAddMode('none'); setError(null); setUrlInput(''); }}
            className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="text-xs text-red-600 mt-1">
          {error}
        </div>
      )}

      {/* Count Display */}
      {mediaUrls.length > 0 && (
        <div className="text-xs text-gray-500">
          {mediaUrls.length} of {maxItems} media items
        </div>
      )}
    </div>
  );
};

export default MediaUploadGrid;

